import * as GeoJSON from 'geojson';
import { LatLngLiteral } from 'leaflet';

import { TimeLogsPeriods } from '../views/time/TimeSettings/config';
import type { Address } from './common';

export enum ClockOutOutsideSettings {
  FLEXIBLE = 'flexible',
  FIRM = 'firm',
  STRICT = 'strict',
}

export enum TrackingType {
  BREADCRUMBS = 'breadcrumbs',
  IN_OUT = 'in_and_out',
  NO_TRACKING = 'no_tracking',
}

export interface TrackingTypeOption {
  value: TrackingType;
  name: string;
}

export interface OnboardingLocation {
  name: string;
  address: Address;
  payScheduleId?: string;
  teamMembersCount: number;
  // A location is set up with at least one shift; the onboarding lets you stack more
  // on top before saving. The first one goes out with the `create site` call, the rest
  // are created against the new site afterwards.
  shifts: Shift[];
  tracking_type?: TrackingTypeOption;
  approver?: string;
  polygon: GeoJSON.Polygon | null;
  locationManagers: {
    uuid: string;
    name: string;
  }[];
}

export interface ManualClockProps {
  selectedWorker?: { name: string; id: string };
  withPhoto?: boolean;
  timestamp: Date | 'now' | null;
  action: 'in' | 'out';
  verifiedLocation?: boolean;
  location?: TimeAndAttendanceLocation;
  shift?: { name: string; id: string };
  /** Bulk clock-out only: the chosen location ids. */
  bulkLocationIds?: string[];
}

export interface TimeAndAttendanceLocation {
  name: string;
  address: Address;
  center?: LatLngLiteral;
  teamMembersCount?: number;
  shifts?: Shift[];
  uuid?: string;
  active?: boolean;
  tracking_type?: TrackingTypeOption;
  polygon?: GeoJSON.Polygon | null;
  created_at?: string;
  clock_out_settings?: ClockOutOutsideSettings;
}

export interface Shift {
  name: string;
  uuid?: string;
  deleted_at?: string;
  regular: ShiftScheduleDetails;
  overtime: ShiftScheduleDetails;
  basic_amount?: number | null;
  basic_unit: string;
  overtime_amount?: number | null;
  overtime_unit: string;
  breaks?: Break[];
  rules?: (Record<string, any> & { id: string; name: string; active: boolean })[];
}

export interface ShiftScheduleDetails {
  startTime?: string | null;
  endTime?: string | null;
  schedule: ShiftSchedule;
}

export interface Break {
  name: string;
  minutes: number;
  paid: boolean;
}

export interface ShiftSchedule {
  monday: DailySchedule;
  tuesday: DailySchedule;
  wednesday: DailySchedule;
  thursday: DailySchedule;
  friday: DailySchedule;
  saturday: DailySchedule;
  sunday: DailySchedule;
}

export interface DailySchedule {
  start: string;
  end: string;
  hour_type?: string;
}

export type SiteShiftRule = {
  id: string;
  name: string;
  active: boolean;
} & Record<string, any>;

export interface SiteShift {
  name: string;
  uuid?: string;
  shift_id?: string;
  deleted_at?: string;
  basic_amount: number | null;
  basic_unit: string;
  overtime_amount: number | null;
  overtime_unit: string;
  schedule: SiteShiftSchedule;
  breaks?: Break[];
  rules?: SiteShiftRule[];
}

export interface SiteShiftSchedule {
  monday: SiteDaySchedule[];
  tuesday: SiteDaySchedule[];
  wednesday: SiteDaySchedule[];
  thursday: SiteDaySchedule[];
  friday: SiteDaySchedule[];
  saturday: SiteDaySchedule[];
  sunday: SiteDaySchedule[];
}

export interface SiteDaySchedule {
  hour_type: string;
  start: {
    hours: number;
    minutes: number;
  };
  end: {
    hours: number;
    minutes: number;
  };
}

export interface BasicSite {
  name: string;
  uuid?: string;
  description?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  address_line_4?: string;
  address_snippet?: string;
  deleted_at?: string;
  country?: string;
  lat?: number;
  lon?: number;
  area_related: boolean;
  // POLYGON((0 0, 0 1, 0 2, 0 3, 0 0))
  geo_polygon?: string | null;
  // POINT(-0.1430083 51.50740649999999)
  geo_point?: string | null;
  tracking_type?: TrackingType;
  created_at?: string;
}

export interface CreateSite extends BasicSite {
  shift: SiteShift;
}

export interface Site extends BasicSite {
  shifts: SiteShift[];
  clock_out_settings?: ClockOutOutsideSettings;
}

export interface TimeMember {
  user_id: string;
  uuid?: string;
  shift_id?: string;
  shift_name?: string;
  site_id?: string;
  site_name?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  gender?: string;
  date_of_birth?: string;
  basic_amount?: number;
  basic_unit?: string;
  overtime_amount?: number;
  overtime_unit?: string;
  avatar?: string;
  photo?: string;
}

export interface AddTimeWorkerData {
  user_id: string;
  basic_amount: number | null;
  basic_unit: string | null;
  overtime_amount: number | null;
  overtime_unit: string | null;
}

export interface ActivityLogEvent {
  weekday: string;
  day: string;
  shift_id: string;
  periods: ActivityLogEventPeriod[];
}

export interface ActivityLogEventPeriod {
  basic_hours: number;
  basic_minutes: number;
  check_in: EventTimeStamp;
  check_out: EventTimeStamp;
  overtime_hours: number;
  overtime_minutes: number;
  site: {
    name: string;
    description: string;
    uuid: string;
  };
}

export interface EmployeeActivityLog {
  weekday: string;
  day: string;
  basic_hours: number;
  basic_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
  periods: ActivityLogEventPeriod[];
}
export interface EmployeeActivityLogEventPeriod extends ActivityLogEventPeriod {
  clocked_in_hours: number;
  clocked_in_minutes: number;
}

interface EventTimeStamp {
  timestamp: string;
  event_type: 'check_in' | 'check_out';
  face_id: string;
  icon_url: string;
  face_verified: boolean;
  within_area: boolean;
  manually_added: boolean;
}

export interface ActivityLogEventTotals {
  basic_hours: number;
  basic_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
}

export enum TrackEventType {
  TRACKING = 'tracking',
  CLOCK_IN = 'check_in',
  CLOCK_OUT = 'check_out',
  LAST_EVENT = 'last_event',
}

export enum HourType {
  BASIC = 'basic',
  OVERTIME = 'overtime',
  OUT_OF_BOUNDS = 'out_of_bounds',
}

export interface TrackEvent {
  user_id: string;
  site_id: string;
  shift_id: string;
  event_type: TrackEventType;
  hour_type: HourType;
  within_area: boolean;
  geo_point: string;
  interval: number;
  timestamp: string;
  device: string;
  platform: string;
  model: string;
  lat?: number;
  lon?: number;
  icon_url?: string;
  fact_id?: string;
  face_verified?: boolean;
}

export enum TimeLogStatus {
  NOT_APPROVED = 'not_approved',
  APPROVED = 'approved',
  PARTLY_APPROVED = 'partly_approved',
}

export interface TimeLogByPeriod {
  basic_hours: number;
  basic_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
  clocked_in_hours: number;
  clocked_in_minutes: number;
  period: DailySchedule;
  status: TimeLogStatus;
  total_hours: number;
  workers: number;
  clocked_in_hours_decimals: number;
  basic_hours_decimals: number;
  overtime_hours_decimals: number;
  total_hours_decimals: number;
  approved_by?: string[] | null;
  approved_at?: string | null;
}

export interface TimeLogBySite {
  basic_hours: number;
  basic_minutes: number;
  overtime_hours: number;
  overtime_minutes: number;
  clocked_in_hours: number;
  clocked_in_minutes: number;
  period: DailySchedule;
  status: TimeLogStatus;
  total_hours: number;
  workers: number;
  site_id: string;
  site_name: string;
  clocked_in_hours_decimals: number;
  basic_hours_decimals: number;
  overtime_hours_decimals: number;
  total_hours_decimals: number;
  approved_by?: string[] | null;
  approved_at?: string | null;
}

export interface WorkerDayLog {
  date: string;
  hours: number;
  minutes: number;
  decimals: number;
}

export interface TimeLogByWorker {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  basic: WorkerDayLog[];
  overtime: WorkerDayLog[];
  total_hours: number;
  total_minutes: number;
  total_clocked_in_hours: number;
  total_clocked_in_minutes: number;
  total_clocked_in_hours_decimals: number;
  total_hours_decimals: number;
  period: DailySchedule;
  status: TimeLogStatus;
  check_in_ok: boolean;
  check_out_ok: boolean;
  face_in_ok: boolean;
  face_out_ok: boolean;
  auto_clock_out: boolean;
  manual_clock_out: boolean;
  check_in_ok_agg: boolean[];
  check_out_ok_agg: boolean[];
  face_in_ok_agg: boolean[];
  face_out_ok_agg: boolean[];
  manual_clock_out_agg: boolean[];
  auto_clock_out_agg: boolean[];
  checked_in_since: string | null;
  avatar?: string;
}

export interface TimeWorker {
  user_id: string;
  avatar: string | null;
  gender?: string | null;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  shifts: TimeWorkerShift[];
}

export interface TimeWorkerShift {
  company_id?: string;
  shift: TimeWorkerShiftItem;
  site: {
    site_id: string;
    name: string;
  };
  current_rates: CurrentRates;
}

export interface CurrentRates {
  basic: TimeWorkerShiftRate;
  overtime: TimeWorkerShiftRate;
}

export interface TimeWorkerShiftItem {
  shift_id: string;
  name: string;
}

export interface TimeWorkerShiftRate {
  rate: number | null;
  amount?: number | null;
  unit: string;
}

export interface TimeLogByShiftDateItemLog {
  approved: boolean;
  basic_amendment_hours: number;
  basic_amendment_minutes: number;
  basic_amendment_hours_decimals: number;
  basic_hours: number;
  basic_minutes: number;
  basic_hours_decimals: number;
  checked_in: string;
  checked_out: null;
  company_id: string;
  overtime_amendment_hours: number;
  overtime_amendment_minutes: number;
  overtime_amendment_hours_decimals: number;
  overtime_hours: number;
  overtime_minutes: number;
  overtime_hours_decimals: number;
  clocked_in_hours: number;
  clocked_in_minutes: number;
  clocked_in_hours_decimals: number;
  pulled: boolean;
  shift_id: string;
  shift_name: string;
  site_id: string;
  site_name: string;
  check_in_ok: boolean;
  check_out_ok: boolean;
  face_in_ok: boolean;
  face_out_ok: boolean;
  manual_clock_out: boolean;
  auto_clock_out: boolean;
  shift_schedule?: SiteDaySchedule[];
}

export interface TimeLogByShiftDateItem {
  date: string;
  weekday: string;
  time_logs: TimeLogByShiftDateItemLog[];
  period: DailySchedule;
}

export interface TimeLogByShift {
  user_id: string;
  week: number;
  year: number;
  date_items: TimeLogByShiftDateItem[];
  totals: TimeTotalInfoProps;
}

export interface AddMemberForm {
  userId: string;
  shift_id: string;
  basic_amount: number | null;
  basic_unit: string | null;
  overtime_amount: number | null;
  overtime_unit: string | null;
}

export enum PullTimelogsStrategy {
  APPEND = 'append',
  REPLACE = 'replace',
}

export enum TimeLogIssue {
  LOCATION = 'Clocked Out outside of work location',
  FACE = 'FaceMatch not verified',
  AUTOMATED = 'Automated Clocked Out',
}

export interface TimeTotalInfoProps {
  basic_hours: number;
  basic_hours_decimals: number;
  basic_minutes: number;
  overtime_hours: number;
  overtime_hours_decimals: number;
  overtime_minutes: number;
  total_hours: number;
  total_hours_decimals: number;
  total_minutes: number;
  total_clocked_in_hours: number;
  total_clocked_in_hours_decimals: number;
  total_clocked_in_minutes: number;
  total_amendments_hours: number;
  total_amendments_minutes: number;
  total_amendments_decimals: number;
  total_workers: number;
}

export interface TimePeriodSettings {
  weekday_start?: string | null;
  period_type: TimeLogsPeriods;
  period_start_date?: string | null;
}

export interface WorkersWorkedTimeOverview {
  start_date: string;
  end_date: string;
  total_pay: number;
  total_basic_hours: number;
  total_overtime_hours: number;
  average_hours: number;
  total_workers: number;
}

export interface TopPerformingWorker {
  uuid: string;
  clocked_hours: number;
  full_name: string;
  gender?: string;
  avatar?: string;
  cnaViewWorker?: boolean;
}
export interface TopPerformingHours {
  overtime_hours: number;
  regular_hours: number;
  approved_hours: number;
  pending_hours: number;
}
export interface TopPerformingTeamMembersLatWeekDTO extends TopPerformingHours {
  top_performing_workers: TopPerformingWorker[];
}

export interface TimePeriod {
  approved: boolean;
  auto_check_out: boolean;
  basic: number;
  basic_hours: number;
  basic_rate: number;
  check_in_ok: boolean;
  check_out_ok: boolean;
  checked_in: string;
  checked_out: string;
  company_id: string;
  created_at: string;
  face_in_ok: boolean;
  face_out_ok: boolean;
  manual_check_in: boolean;
  manual_check_out: boolean;
  manually_added: boolean;
  overtime: number;
  overtime_hours: number;
  overtime_rate: number;
  shift_id: string;
  site_id: string;
  total: number;
  total_hours: number;
  unpaid_break: boolean;
  updated_at: string;
  user_id: string;
  uuid: string;
  workday: string;
  check_in_accuracy?: string;
  check_out_accuracy?: string;
}

export interface LocationUserDTO {
  user_id: string;
  full_name: string;
  photo_url: string;
  location: string;
  last_clock_event: string;
  status: string;
  gender: string;
  shift_id: string;
  site_id: string;
  is_removable: boolean;
}

export interface LocationWithGeo {
  uuid: string;
  name: string;
  description: string | null;
  address: string;
  geo_polygon: string | null;
  geo_point: string | null;
}

export interface LocationAndTeamsOption {
  uuid: string;
  name: string;
  is_active: boolean;
}
export interface LocationAndTeamsFilterDTO {
  uuid: string;
  company_id: string;
  user_id: string;
  locations: LocationAndTeamsOption[];
  teams: LocationAndTeamsOption[];
}

export interface LocationAndTeamsFilterState {
  locations: LocationAndTeamsOption[];
  teams: LocationAndTeamsOption[];
}

export enum RecurrenceType {
  EVERY_DAY = 'EVERY_DAY',
  EVERY_WEEKDAY = 'EVERY_WEEKDAY',
  CUSTOM = 'CUSTOM',
}

export interface ScheduledNotification {
  channel: 'email' | 'sms';
  name: string;
  config: {
    all_locations: boolean;
    location_ids: string[];
    recurrence_type: RecurrenceType;
    custom_days: number[];
    send_time: string;
    recipient_emails: string[];
    recipient_user_ids: string[];
  };
}
